import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";

export interface ModalProps {
  open: boolean;
  /** Wird bei Escape, Klick auf den Hintergrund und ueber die Aktionen gerufen. */
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Buttons am unteren Rand. */
  actions?: ReactNode;
  /**
   * Verhindert Schliessen per Escape und Hintergrundklick. Fuer Entscheidungen,
   * die ausdruecklich getroffen werden muessen -- etwa eine eingehende
   * Verbindungsanfrage, bei der versehentliches Wegklicken kein "Ablehnen" sein
   * soll.
   */
  dismissible?: boolean;
}

/**
 * Modal auf Basis des nativen <dialog>.
 *
 * Bewusst nicht selbst gebaut: `showModal()` liefert Fokusfalle, Escape-Taste,
 * Inertheit des Hintergrunds und die Top-Layer-Darstellung vom Browser. Jede
 * Eigenimplementierung davon ist erfahrungsgemaess luecken- und fehlerhaft.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
  dismissible = true,
}: ModalProps): React.ReactElement {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Escape loest `cancel` aus. Bei einer nicht wegklickbaren Entscheidung
    // wird das unterbunden, sonst nach aussen gemeldet.
    const handleCancel = (event: Event): void => {
      event.preventDefault();
      if (dismissible) onClose();
    };
    // `close` faengt auch die Faelle ab, in denen der Browser selbst schliesst.
    const handleClose = (): void => {
      if (open) onClose();
    };

    dialog.addEventListener("cancel", handleCancel);
    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      dialog.removeEventListener("close", handleClose);
    };
  }, [dismissible, onClose, open]);

  // Klick auf den Backdrop: das Ereignis trifft das <dialog> selbst, weil der
  // Backdrop kein eigenes Element ist. Ein Klick auf den Inhalt wird davon
  // ueber den Zielvergleich unterschieden.
  function handleClick(event: React.MouseEvent<HTMLDialogElement>): void {
    if (!dismissible) return;
    if (event.target === ref.current) onClose();
  }

  return (
    <dialog ref={ref} className="modal" aria-labelledby={titleId} onClick={handleClick}>
      <h2 id={titleId} style={{ fontSize: "var(--text-h3)" }}>
        {title}
      </h2>
      <div style={{ marginTop: "var(--space-2)" }}>{children}</div>
      {actions ? <div className="modal__actions">{actions}</div> : null}
    </dialog>
  );
}
